import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { fmtDate } from "@/constants/labels";
import { AlertTriangle, Search, Trash2, ShieldAlert, Plus, X, History } from "lucide-react";

const ROLE_LABELS = { entreprise: "Entreprise", prestataire: "Prestataire", admin: "Admin" };

export default function AdminPurge() {
  const [prefixes, setPrefixes] = useState([""]);
  const [regexes, setRegexes] = useState([]);
  const [emails, setEmails] = useState([]);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  const criteria = () => ({
    prefixes: prefixes.filter(Boolean),
    regexes: regexes.filter(Boolean),
    emails: emails.filter(Boolean),
  });

  const runPreview = async () => {
    const body = criteria();
    if (!body.prefixes.length && !body.regexes.length && !body.emails.length) {
      toast.error("Fournissez au moins un critère.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/purge/preview", body);
      setPreview(data);
      // Par défaut on présélectionne tous les non-admins
      const nonAdmins = new Set(data.users.filter((u) => u.role !== "admin").map((u) => u.id));
      setSelected(nonAdmins);
      setConfirmPhrase("");
      if (data.users.length === 0) toast.info("Aucun compte trouvé pour ces critères.");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setLoading(false);
    }
  };

  const runExecute = async () => {
    if (!preview || selected.size === 0) return;
    if (confirmPhrase !== (preview.confirm_phrase_required || "SUPPRIMER")) {
      toast.error(`Tapez exactement "${preview.confirm_phrase_required}" pour confirmer.`);
      return;
    }
    setExecuting(true);
    try {
      const { data } = await api.post("/admin/purge/execute", {
        user_ids: [...selected],
        confirm_phrase: confirmPhrase,
        criteria_echo: criteria(),
      });
      toast.success(`Purge effectuée : ${data.deleted_users_count} compte(s) supprimé(s).`);
      setPreview(null);
      setSelected(new Set());
      setConfirmPhrase("");
      loadLogs();
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setExecuting(false);
    }
  };

  const loadLogs = async () => {
    try {
      const { data } = await api.get("/admin/purge/logs");
      setLogs(data);
    } catch { /* silencieux */ }
  };
  useEffect(() => { loadLogs(); }, []);

  const toggleSel = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const updateAt = (list, setter, i, v) => {
    const next = [...list]; next[i] = v; setter(next);
  };
  const removeAt = (list, setter, i) => {
    const next = list.filter((_, idx) => idx !== i);
    setter(next.length ? next : [""]);
  };

  const CriterionRow = ({ list, setter, placeholder, testidPrefix }) => (
    <div className="space-y-2">
      {list.map((v, i) => (
        <div key={i} className="flex gap-2">
          <input
            type="text"
            value={v}
            onChange={(e) => updateAt(list, setter, i, e.target.value)}
            placeholder={placeholder}
            className="input-base flex-1 font-mono text-sm"
            data-testid={`${testidPrefix}-input-${i}`}
          />
          <button
            type="button"
            onClick={() => removeAt(list, setter, i)}
            className="btn-outline px-2"
            title="Retirer"
            data-testid={`${testidPrefix}-remove-${i}`}
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setter([...list, ""])}
        className="btn-outline text-xs"
        data-testid={`${testidPrefix}-add`}
      >
        <Plus size={12} /> Ajouter
      </button>
    </div>
  );

  return (
    <div className="mt-4 space-y-4" data-testid="admin-purge-panel">
      <div className="card p-4 border-l-4 border-[#E8710A]">
        <div className="flex items-start gap-3">
          <ShieldAlert size={22} className="text-[#E8710A] mt-0.5" />
          <div className="text-sm text-[#3E4C59]">
            <div className="font-semibold text-[#1F2933] mb-1">Purge de comptes — action irréversible</div>
            <div>
              Utilisez ce panneau pour supprimer des comptes de test ainsi que toutes leurs données liées
              (candidatures, missions, avis, notifications, e-mails, besoins, jalons, contre-offres…).
              Procédure : <strong>1. Prévisualiser</strong> vos critères, <strong>2. Sélectionner</strong> les
              comptes, <strong>3. Confirmer</strong> en tapant exactement <code className="bg-white px-1">SUPPRIMER</code>.
              Les comptes admin sont exclus automatiquement.
            </div>
          </div>
        </div>
      </div>

      <div className="card p-4">
        <div className="font-semibold text-[#1F2933] mb-3">Critères</div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs uppercase text-[#3E4C59] mb-2">Préfixe (« prénom nom » commence par)</div>
            <CriterionRow list={prefixes} setter={setPrefixes} placeholder="Jean Test" testidPrefix="purge-prefix" />
          </div>
          <div>
            <div className="text-xs uppercase text-[#3E4C59] mb-2">Regex sur « prénom nom »</div>
            <CriterionRow list={regexes.length ? regexes : [""]} setter={setRegexes} placeholder="^N a$" testidPrefix="purge-regex" />
          </div>
          <div>
            <div className="text-xs uppercase text-[#3E4C59] mb-2">E-mail (wildcard * autorisé)</div>
            <CriterionRow list={emails.length ? emails : [""]} setter={setEmails} placeholder="*@example.test" testidPrefix="purge-email" />
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={runPreview}
            disabled={loading}
            className="btn-primary"
            data-testid="purge-preview-btn"
          >
            <Search size={14} /> {loading ? "Recherche…" : "Prévisualiser"}
          </button>
        </div>
      </div>

      {preview && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-[#1F2933]">
              Aperçu : {preview.users.length} compte(s) trouvé(s) — {preview.total_related} document(s) lié(s)
            </div>
            <button
              onClick={() => { setPreview(null); setSelected(new Set()); }}
              className="btn-outline text-xs"
              data-testid="purge-preview-close"
            >
              Fermer l'aperçu
            </button>
          </div>

          {preview.users.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="table-compact w-full">
                  <thead>
                    <tr>
                      <th className="w-8">
                        <input
                          type="checkbox"
                          checked={selected.size === preview.users.filter(u => u.role !== "admin").length && selected.size > 0}
                          onChange={(e) => {
                            if (e.target.checked) setSelected(new Set(preview.users.filter(u => u.role !== "admin").map(u => u.id)));
                            else setSelected(new Set());
                          }}
                          data-testid="purge-select-all"
                        />
                      </th>
                      <th>E-mail</th>
                      <th>Nom</th>
                      <th>Rôle</th>
                      <th>Raison sociale</th>
                      <th>Créé</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.users.map((u) => (
                      <tr key={u.id} data-testid={`purge-user-row-${u.id}`}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.has(u.id)}
                            disabled={u.role === "admin"}
                            onChange={() => toggleSel(u.id)}
                            data-testid={`purge-select-${u.id}`}
                          />
                        </td>
                        <td className="text-xs">{u.email}</td>
                        <td>{[u.prenom, u.nom].filter(Boolean).join(" ") || "—"}</td>
                        <td>
                          {u.role === "admin"
                            ? <span className="badge-danger">Admin (protégé)</span>
                            : ROLE_LABELS[u.role] || u.role}
                        </td>
                        <td className="text-xs">{u.raison_sociale || "—"}</td>
                        <td className="text-xs">{fmtDate(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {Object.entries(preview.counts).filter(([, n]) => n > 0).map(([k, n]) => (
                  <div key={k} className="bg-[#F5F7FA] px-2 py-1 rounded">
                    <span className="text-[#3E4C59]">{k}:</span> <strong>{n}</strong>
                  </div>
                ))}
              </div>

              <div className="mt-6 border-t pt-4">
                <div className="flex items-center gap-2 text-[#E8710A] font-semibold mb-2">
                  <AlertTriangle size={16} /> Confirmation obligatoire
                </div>
                <div className="text-sm text-[#3E4C59] mb-2">
                  Vous êtes sur le point de supprimer <strong>{selected.size}</strong> compte(s) et
                  toutes leurs données liées. Tapez exactement <code className="bg-[#F5F7FA] px-1">{preview.confirm_phrase_required}</code> pour confirmer.
                </div>
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={confirmPhrase}
                    onChange={(e) => setConfirmPhrase(e.target.value)}
                    placeholder={preview.confirm_phrase_required}
                    className="input-base font-mono w-64"
                    data-testid="purge-confirm-input"
                  />
                  <button
                    onClick={runExecute}
                    disabled={executing || selected.size === 0 || confirmPhrase !== preview.confirm_phrase_required}
                    className="btn-danger"
                    data-testid="purge-execute-btn"
                  >
                    <Trash2 size={14} /> {executing ? "Suppression…" : `Supprimer ${selected.size} compte(s)`}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="card p-4">
        <button
          onClick={() => setShowLogs(!showLogs)}
          className="flex items-center gap-2 text-sm font-semibold text-[#1B3A5C]"
          data-testid="purge-logs-toggle"
        >
          <History size={14} /> Historique des purges ({logs.length}) {showLogs ? "▼" : "▶"}
        </button>
        {showLogs && (
          <div className="mt-3 overflow-x-auto">
            {logs.length === 0 ? (
              <div className="text-sm text-[#3E4C59]">Aucune purge effectuée.</div>
            ) : (
              <table className="table-compact w-full">
                <thead><tr><th>Date</th><th>Admin</th><th>Comptes</th><th>Total lié</th><th>Critères</th></tr></thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} data-testid={`purge-log-row-${l.id}`}>
                      <td className="text-xs">{fmtDate(l.when)}</td>
                      <td className="text-xs">{l.who_email}</td>
                      <td><strong>{l.deleted?.users || 0}</strong></td>
                      <td>{Object.entries(l.deleted || {}).filter(([k]) => k !== "users").reduce((s, [, n]) => s + n, 0)}</td>
                      <td className="text-xs font-mono">{JSON.stringify(l.criteria || {})}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
